import { DeleteIcon, Download, Move, Share } from '@mybridge/icons';
import { CopyIcon } from '@mybridge/icons/Copy';
import { LinkIcon } from '@mybridge/icons/Link';
import { RenameIcon } from '@mybridge/icons/Rename';
import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Portal,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from '@mybridge/ui';
import { useToast, useDisclosure } from '@mybridge/ui/hooks';
import { useContext, useEffect, useMemo, useState } from 'react';
import { isFile, isRootDir } from 'v4/components/utils';
import { copyTextToClipboard } from 'v4/lib/commons';
import { DiskContext } from '../../context';
import { DiskFileInfoSection } from '../../file-info-section';
import { useDispatch, useSelector } from 'react-redux';
import { useDownloadFile } from 'v4/components/hooks/useDownloadFile';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { getTeamMemberList } from 'v4/store/actions/teamCommunity.actions';
import { getSharedItemUsers } from 'v4/store/actions/myDisk.Action';
import { useBranchLink } from 'v4/components/branch-sdk/branch-create';

export const MyDiskItemMenu = ({
  children,
  file,
  accessType = 'W',
  isOwner,
  permissionsType,
  teamDetails,
  ...props
}) => {
  const {
    shareDisc,
    deleteDisc,
    selectedFile,
    renameDisc,
    moveDisc,
    copyDisc,
    binDisc,
    download,
    setSelectedTeam_,
    setShowPreview,
    downloadDisc,
    selectedMainDirType,
    refetchSharedFiles
  } = useContext(DiskContext);
  const { t } = useTranslator();
  const infoDisc = useDisclosure();
  const dispatch = useDispatch();
  const { userProfileInfo } = useSelector((s) => s.userProfile) ?? {};
  const [accessTypeTeam, setAccessTypeTeam] = useState('W');
  const [sharedItemUsersData, setSharedItemUsersData] = useState(null);
  const { isReady, createBranchLink } = useBranchLink();
  
  useEffect(() => {
    dispatch(getTeamMemberList({
      for_team_community_id: teamDetails?.id,
      list_type: 'member',
      user_id: userProfileInfo?.id,
    })).then((res) => {
      
      setAccessTypeTeam(res?.payload?.disk_status);
    })
  },[teamDetails?.id, userProfileInfo?.id]);

  // Fetch shared item users when selectedFile changes
  useEffect(() => {
    if (selectedFile?.created_by && file && selectedFile?.id === file?.id) {
      fetchSharedItemUsers(selectedFile.created_by);
    }
  }, [selectedFile?.created_by, selectedFile?.id, file?.id]);

  const fetchSharedItemUsers = async (fileId) => {
    try {
      const response = await dispatch(getSharedItemUsers(fileId));
      if (response?.payload?.results) {
        setSharedItemUsersData(response.payload.results);
      }
    } catch (error) {
      console.error("Error fetching shared item users:", error);
    }
  };
  
  const { onDownload } = download ?? {};

  const toast = useToast({
    position: 'top',
  });

  const handleGenerateLink = async () => {
    try {
      const url = await createBranchLink({
        itemType: 'disk',
        itemId: selectedFile?.id,
        itemData: {
          "accessType": 1,
          "name": selectedFile?.name,
          "type": "F",
          "file": selectedFile?.file
        },
      });
      if (url) {
        copyLink(url);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const copyLink = async (url) => {
    try {
      await copyTextToClipboard(url || selectedFile?.file);
      toast({ title: t('myDisk.linkCopiedSuccessfully') || 'Link copied successfully!', status: 'success',position: 'top' });
    } catch (e) {
      toast({ title: t('toast.failedToCopyLink') || 'Failed to copy link', status: 'error' });
    }
  };

  const onClickMoveFile = () => {
    moveDisc?.onOpen();
    setSelectedTeam_(teamDetails);
    setShowPreview(false);
  };
  const onClickCopyFile = () => {
    copyDisc?.onOpen();
    setSelectedTeam_(teamDetails);
    setShowPreview(false);
  };

  // open download dialog
  const downloadFile = async () => {
    downloadDisc?.onOpen();
  };

  // const downloadFile = async () => {
  //   if (typeof window === 'undefined') return;

  //   onDownload();
  //   fetch(selectedFile?.file)
  //     .then((response) => response.blob())
  //     .then((blob) => {
  //       // Create a blob URL for the PDF data
  //       var url = window.URL.createObjectURL(blob);

  //       // Create a link element to trigger the download
  //       var a = document.createElement('a');
  //       a.href = url;
  //       a.download = `${selectedFile.name}${extn}`; // Set the desired file name
  //       document.body.appendChild(a);

  //       // Trigger a click event on the link element to initiate the download
  //       a.click();

  //       // Clean up by revoking the blob URL and removing the link element
  //       window.URL.revokeObjectURL(url);
  //       document.body.removeChild(a);
  //     })
  //     .catch((error) => {
  //       console.error('Failed to download the PDF file: ', error);
  //     });
  // };
  // console.log('permissionsType', accessType);
  const getEffectiveAccessType = (selectedFile, sharedItemUsersData) => {
    if (!selectedFile || !sharedItemUsersData) return null;
    
    // First check if the current file has direct access
    const directAccess = sharedItemUsersData.find(item => item.id === selectedFile.id);
    if (directAccess) {
      return directAccess.access_type;
    }
    
    // If no direct access, check if parent has access and inherit it
    if (selectedFile.parent) {
      const parentAccess = sharedItemUsersData.find(item => item.id === selectedFile.parent);
      if (parentAccess) {
        return parentAccess.access_type;
      }
    }
    
    return null;
  };

  const effectiveAccessType = getEffectiveAccessType(selectedFile, sharedItemUsersData);

  const items = useMemo(() => {
    let menu = [
      {
        title: 'Info',
        Icon: InfoOutlineIcon,
        props: { onClick: infoDisc?.onOpen },
        displayMenu: true,
      },
    ];
    if ((accessType === 'W') || (accessTypeTeam === 'W' && selectedMainDirType === 'team-disk')) {
      selectedMainDirType!=='team-disk' && (
        menu.push({
          title: t('post.share') || 'Share',
          Icon: Share,
          props: { onClick: shareDisc?.onOpen },
          // Updated condition to include effectiveAccessType
          displayMenu: (isOwner || permissionsType === 2 || (selectedMainDirType === 'shared-disk' && effectiveAccessType === 2)),
        })
      )
      if (isFile(selectedFile ?? {})) {
        menu.push({
          title: t('profilePhotosVideos.download') || 'Download',
          Icon: Download,
          props: { onClick: downloadFile },
          displayMenu: true,
        });
      }
      if (isFile(selectedFile ?? {})) {
        menu.push({
          title: t('post.copyLink') || 'Copy Link',
          Icon: LinkIcon,
          props: { onClick: handleGenerateLink },
          // Updated condition to include effectiveAccessType
          displayMenu: (isOwner || permissionsType === 2 || 
            (selectedMainDirType === 'shared-disk' && effectiveAccessType === 2)),
        });
      }
      if (isRootDir(selectedFile)) {
        if (menu.length > 1) menu.push({ type: 'divider' });
        menu.push({
          title: t('myDisk.copy') || 'Copy',
          Icon: CopyIcon,
          props: { onClick: copyDisc?.onOpen },
          displayMenu: true,
        });
      } else {
        if (menu.length > 1) menu.push({ type: 'divider' });
        menu.push({
          title: t('buttons.delete') || 'Delete',
          Icon: DeleteIcon,
          props: { onClick: deleteDisc?.onOpen },
          displayMenu: ((accessType === 'W' && selectedMainDirType !== 'team-disk' && selectedMainDirType !== 'shared-disk') || 
            (selectedMainDirType === 'shared-disk' && effectiveAccessType === 2) || 
            (accessTypeTeam === 'W' && selectedMainDirType === 'team-disk') || 
            (isOwner || permissionsType === 2)),
        });
        menu.push({
          title: t('myDisk.rename') || 'Rename',
          Icon: RenameIcon,
          props: { onClick: renameDisc?.onOpen },
          displayMenu: ((accessType === 'W' && selectedMainDirType !== 'team-disk' && selectedMainDirType !== 'shared-disk') || 
            (selectedMainDirType === 'shared-disk' && effectiveAccessType === 2) || 
            (accessTypeTeam === 'W' && selectedMainDirType === 'team-disk') || 
            (isOwner || permissionsType === 2)),
        });
        menu.push({
          title: t('myDisk.move') || 'Move',
          Icon: Move,
          props: { onClick: onClickMoveFile },
          displayMenu: ((accessType === 'W' && selectedMainDirType !== 'team-disk' && selectedMainDirType !== 'shared-disk') || 
            (selectedMainDirType === 'shared-disk' && effectiveAccessType === 2) || 
            (accessTypeTeam === 'W' && selectedMainDirType === 'team-disk') || 
            (isOwner || permissionsType === 2)),
        });
        menu.push({
          title: t('myDisk.copy') || 'Copy',
          Icon: CopyIcon,
          props: { onClick: onClickCopyFile },
          displayMenu: true,
        });
        menu.push({
          title: t('myDisk.deletedFiles') || 'Deleted Files',
          Icon: DeleteIcon,
          props: { onClick: binDisc?.onOpen },
          displayMenu: ((accessType === 'W' && selectedMainDirType !== 'team-disk' && selectedMainDirType !== 'shared-disk') || 
            (selectedMainDirType === 'shared-disk' && effectiveAccessType === 2) || 
            (accessTypeTeam === 'W' && selectedMainDirType === 'team-disk') || 
            (isOwner)),
        });
      }
    } else if ((accessType === 'R' && selectedMainDirType !== 'team-disk') || (accessTypeTeam === 'R' && selectedMainDirType === 'team-disk')) {
      if (isFile(selectedFile ?? {})) {
        menu.push({
          title: t('myDisk.download') || 'Download',
          Icon: Download,
          props: { onClick: downloadFile },
          displayMenu: true,
        });
        menu.push({ type: 'divider' });
      }
      menu.push({
        title: t('myDisk.copy') || 'Copy',
        Icon: CopyIcon,
        props: { onClick: onClickCopyFile },
        displayMenu: true,
      });
      menu.push({
        title: t('myDisk.rename') || 'Rename',
        Icon: RenameIcon,
        props: { onClick: renameDisc?.onOpen },
        displayMenu: isOwner,
      });
      menu.push({
        title: t('myDisk.move') || 'Move',
        Icon: Move,
        props: { onClick: onClickMoveFile },
        displayMenu: isOwner,
      });
      menu.push({
        title: t('buttons.delete') || 'Delete',
        Icon: DeleteIcon,
        props: { onClick: deleteDisc?.onOpen },
        displayMenu: isOwner,
      });
    }
    return menu;
  }, [selectedFile, accessType, permissionsType, isOwner, selectedMainDirType, accessTypeTeam, sharedItemUsersData, effectiveAccessType, infoDisc?.isOpen]);

  return (
    <>
      <Menu>
        <MenuButton>{children}</MenuButton>
        <Portal>
          <MenuList borderRadius="15" zIndex="2">
            {items?.map((item, index) =>
              item.type === 'divider' ? (
                <MenuDivider key={index} />
              ) : item?.displayMenu ? (
                <MenuItem
                  key={index}
                  {...(item.props ?? {})}
                  icon={<item.Icon width={20} height={20} w="20px" h="20px" />}
                >
                  {item.title}
                </MenuItem>
              ) : (
                <></>
              )
            )}
          </MenuList>
        </Portal>
      </Menu>

      {/* Info Modal */}
      {infoDisc?.isOpen && (
        <Modal isOpen={infoDisc.isOpen} onClose={infoDisc.onClose} isCentered size="md">
          <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
          <ModalContent borderRadius="16px" mx={4}>
            <ModalHeader pb={1} pt={4} fontSize="18px" fontWeight="600" color="#3D5A80">
              File Info
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody py={4}>
              <DiskFileInfoSection />
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};
